import keys from 'lodash/keys';
import type { ChangeEvent, ReactElement, SelectHTMLAttributes } from 'react';

type Props<
  Value extends string,
  Label extends string,
  Options extends Record<Value, Label>
> = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> & {
  value: Value;
  options: Options;
  onValue: (value: Value) => void;
};

export function DropdownSelector<
  Value extends string,
  Label extends string,
  Options extends Record<Value, Label>
>(props: Props<Value, Label, Options>): ReactElement {
  const { value, options, onValue, ...otherProps } = props;

  const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const nextValue = keyPresence(options, e.target.value);
    if (nextValue !== null) {
      onValue(nextValue);
    }
  };
  let selectedValue: null | Value | string = keyPresence(options, value);
  if (selectedValue === null) {
    selectedValue = '';
  }

  return (
    <select {...otherProps} value={selectedValue} onChange={onChange}>
      {keys(props.options).map(optionValue => (
        <option value={optionValue} key={optionValue}>
          {props.options[optionValue]}
        </option>
      ))}
    </select>
  );
}

function keyPresence<Key extends string, Value>(
  map: Record<Key, Value>,
  maybeKey: string
): Key | null {
  // Presence-checking a possibly-absent key on a generically-keyed map, then
  // trusting that a present key belongs to `Key`, needed a `$FlowFixMe` and a
  // `$FlowIgnore` in Flow. Neither error exists in TypeScript: indexing
  // `Record<Key, Value>` with a plain `string` type-checks as-is, and `as Key`
  // permits the same narrowing Flow required a suppression for.
  if (typeof map[maybeKey] !== 'undefined') {
    return maybeKey as Key;
  }
  return null;
}
